import { useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { DateTime } from 'luxon';
import { useEffect } from 'react';

import { useWarehouseContext } from 'common/context/WarehouseContext';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useToast } from 'common/hooks/useToast';

import { taskNotificationDisplayedDateTimeAtom } from './state';
import { useGetTasksCountQuery } from './useGetTasksCountQuery';
import { useIsTaskNotificationEnabledPath } from './useIsTaskNotificationEnabledPath';

export const TASK_NOTIFICATION_TOAST_ID = 'task-notification-toast';
export const HRS_TO_TRIGGER_TASK_NOTIFICATION = 3;
const E2E_TEST_VENUE_IDS = [
  '954c57b4-fe9e-4217-8843-624139338bfc', // WARP E2E Test Venue
];

const shouldTriggerTaskNotification = (
  enabled: boolean,
  hasTasksToComplete: boolean,
  lastToastDisplayedDateTime: string | null,
) => {
  if (!enabled || !hasTasksToComplete) return false;

  if (!lastToastDisplayedDateTime) return true;

  // once a task notification is displayed, we want to wait 3 hours before displaying it again

  const currentDateTime = DateTime.now();

  const newNotificationTriggerDateTime = DateTime.fromISO(lastToastDisplayedDateTime).plus({
    hours: HRS_TO_TRIGGER_TASK_NOTIFICATION,
  });

  return currentDateTime >= newNotificationTriggerDateTime;
};

/**
 * This hook is used to display a toast notification when there are tasks to complete.
 * It is used globally
 */
export const useTaskNotificationToast = () => {
  const f = useFormatMessage();
  const navigate = useNavigate();
  const { addToast, removeToast } = useToast();
  const isTaskNotificationEnabledPath = useIsTaskNotificationEnabledPath();
  const { warehouse: selectedWarehouse } = useWarehouseContext();
  const selectedWarehouseId = selectedWarehouse?.id;
  const taskCounts = useGetTasksCountQuery(selectedWarehouseId);

  const [taskNotificationDisplayedDateTime, setTaskNotificationDisplayedDateTime] = useAtom(
    taskNotificationDisplayedDateTimeAtom,
  );

  const hasTasksToComplete = Boolean(taskCounts?.new) || Boolean(taskCounts?.inProgress);

  const shouldDisplayToast = shouldTriggerTaskNotification(
    isTaskNotificationEnabledPath,
    hasTasksToComplete,
    taskNotificationDisplayedDateTime,
  );

  useEffect(() => {
    // Don't display task notification toast for e2e test venue.
    // It blocks elements on the header of a page, which causes tests to fail
    if (selectedWarehouseId && E2E_TEST_VENUE_IDS.includes(selectedWarehouseId)) return;

    if (shouldDisplayToast) {
      addToast({
        autoClose: false,
        dataTestId: TASK_NOTIFICATION_TOAST_ID,
        description: f('task_management.notification_desc'),
        id: TASK_NOTIFICATION_TOAST_ID,
        onPrimaryButtonClick: () => {
          void navigate({
            to: '/tasks',
            search: { filter: taskCounts?.inProgress ? 'IN_PROGRESS' : 'NEW' },
          });
          removeToast(TASK_NOTIFICATION_TOAST_ID);
        },
        onSecondaryButtonClick: () => removeToast(TASK_NOTIFICATION_TOAST_ID),
        primaryButtonLabel: f('task_management.notification_primary_btn_label'),
        secondaryButtonLabel: f('task_management.notification_secondary_btn_label'),
        title: f('task_management.notification_title'),
        variant: 'info',
      });

      setTaskNotificationDisplayedDateTime(DateTime.now().toISO());
    }

    if (!isTaskNotificationEnabledPath) {
      removeToast(TASK_NOTIFICATION_TOAST_ID);
    }
  }, [
    taskCounts,
    shouldDisplayToast,
    isTaskNotificationEnabledPath,
    taskNotificationDisplayedDateTime,
    addToast,
    setTaskNotificationDisplayedDateTime,
    removeToast,
    f,
    navigate,
    selectedWarehouseId,
  ]);
};
